// @ts-nocheck
import { BACKEND_API_BASE_URL, CLIENT_SOURCE } from "../../utilities/constant";
import { getUserAccessToken } from "../../utilities/setting";
import {
  API_SESSION_REPLAY_START,
  API_SESSION_REPLAY_BATCH,
  API_SESSION_REPLAY_END,
} from "../url";

const FLUSH_INTERVAL_MS = 15000;
const HEARTBEAT_AFTER_MS = 55000;
const FLUSH_PENDING_BYTES = 256 * 1024;
const MAX_BUFFER_BYTES = 5 * 1024 * 1024;
const MAX_EVENTS_PER_BATCH = 500;
const MAX_CHUNKS_PER_BATCH = 10;
const BEACON_SAFE_BYTES = 60 * 1024;
const MAX_BATCH_BYTES = 768 * 1024;

let sessionId = null;
let eventQueue = [];
let chunkQueue = [];
let pendingChunkBytes = 0;
let flushTimer = null;
let flushing = false;
let lastSentAt = 0;
let stashedToken = null;

const url = (path) => `${BACKEND_API_BASE_URL}${path}`;

function token() {
  return getUserAccessToken() || stashedToken;
}

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: token(),
    "x-client-source": CLIENT_SOURCE,
  };
}

async function send(path, body, { keepalive = false } = {}) {
  if (!token()) return 0;
  try {
    const res = await fetch(url(path), {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
      keepalive,
    });
    return res.status;
  } catch {
    return 0;
  }
}

async function post(path, body, opts) {
  const status = await send(path, body, opts);
  return status >= 200 && status < 300;
}

function beacon(path, body) {
  try {
    const tok = token();
    if (!tok || !navigator?.sendBeacon) return false;
    const sep = path.includes("?") ? "&" : "?";
    const beaconUrl = `${url(path)}${sep}token=${encodeURIComponent(tok)}&x-client-source=${encodeURIComponent(CLIENT_SOURCE)}`;
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    return navigator.sendBeacon(beaconUrl, blob);
  } catch {
    return false;
  }
}

export async function startSession(newSessionId, { startedAt, screen, device }) {
  sessionId = newSessionId;
  eventQueue = [];
  chunkQueue = [];
  pendingChunkBytes = 0;
  lastSentAt = Date.now();
  stashedToken = getUserAccessToken() || null;

  if (!flushTimer) {
    flushTimer = setInterval(tick, FLUSH_INTERVAL_MS);
  }

  return post(API_SESSION_REPLAY_START, {
    session_id: newSessionId,
    started_at: startedAt,
    screen,
    device,
  });
}

export function enqueueEvent(event) {
  if (!sessionId) return;
  eventQueue.push(event);
  if (eventQueue.length > MAX_EVENTS_PER_BATCH * 4) {
    eventQueue = eventQueue.slice(-MAX_EVENTS_PER_BATCH * 2);
  }
}

export function enqueueChunk(chunk) {
  if (!sessionId) return;
  chunkQueue.push(chunk);
  pendingChunkBytes += chunk.payload.length;

  while (pendingChunkBytes > MAX_BUFFER_BYTES && chunkQueue.length > 1) {
    const dropped = chunkQueue.shift();
    pendingChunkBytes -= dropped.payload.length;
  }

  if (pendingChunkBytes > FLUSH_PENDING_BYTES) flush();
}

async function flush({ keepalive = false } = {}) {
  if (!sessionId || flushing) return;
  if (!eventQueue.length && !chunkQueue.length) return;
  flushing = true;

  const events = eventQueue.slice(0, MAX_EVENTS_PER_BATCH);
  const chunks = [];
  let bytes = 0;
  for (const c of chunkQueue.slice(0, MAX_CHUNKS_PER_BATCH)) {
    if (chunks.length && bytes + c.payload.length > MAX_BATCH_BYTES) break;
    chunks.push(c);
    bytes += c.payload.length;
  }

  const status = await send(
    API_SESSION_REPLAY_BATCH(sessionId),
    { events, chunks },
    { keepalive }
  );
  const ok = status >= 200 && status < 300;

  if (status === 413 && chunks.length) {
    chunkQueue = chunkQueue.slice(chunks.length);
    pendingChunkBytes = chunkQueue.reduce((n, c) => n + c.payload.length, 0);
    flushing = false;
    flush({ keepalive });
    return;
  }

  if (ok) {
    eventQueue = eventQueue.slice(events.length);
    chunkQueue = chunkQueue.slice(chunks.length);
    pendingChunkBytes = chunkQueue.reduce((n, c) => n + c.payload.length, 0);
    lastSentAt = Date.now();
  }
  flushing = false;

  if (ok && (eventQueue.length || chunkQueue.length)) flush({ keepalive });
}

async function tick() {
  if (!sessionId) return;
  if (eventQueue.length || chunkQueue.length) {
    await flush();
    return;
  }
  if (Date.now() - lastSentAt >= HEARTBEAT_AFTER_MS) {
    const ok = await post(API_SESSION_REPLAY_BATCH(sessionId), {
      events: [],
      chunks: [],
    });
    if (ok) lastSentAt = Date.now();
  }
}

export function flushOnExit() {
  if (!sessionId) return;
  if (flushing) return;
  if (!eventQueue.length && !chunkQueue.length) return;

  const events = eventQueue.slice(0, MAX_EVENTS_PER_BATCH);
  const smallChunks = [];
  let bytes = 0;
  for (let i = chunkQueue.length - 1; i >= 0; i--) {
    const c = chunkQueue[i];
    if (bytes + c.payload.length > BEACON_SAFE_BYTES) break;
    smallChunks.unshift(c);
    bytes += c.payload.length;
  }

  const body = { events, chunks: smallChunks, truncated: smallChunks.length < chunkQueue.length };

  // keepalive fetch first (bigger budget), beacon as last resort
  const batchPath = API_SESSION_REPLAY_BATCH(sessionId);
  post(batchPath, body, { keepalive: true }).then((ok) => {
    if (!ok) beacon(batchPath, body);
  });

  
  eventQueue = eventQueue.slice(events.length);
  chunkQueue = chunkQueue.slice(0, chunkQueue.length - smallChunks.length);
  pendingChunkBytes = chunkQueue.reduce((n, c) => n + c.payload.length, 0);
}

export async function endSession(reason = "explicit") {
  if (!sessionId) return;
  const sid = sessionId;

  flushOnExit();

  const body = { ended_at: new Date().toISOString(), end_reason: reason };
  const ok = await post(API_SESSION_REPLAY_END(sid), body, { keepalive: true });
  if (!ok) beacon(API_SESSION_REPLAY_END(sid), body);

  sessionId = null;
  stashedToken = null;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

export function getActiveSessionId() {
  return sessionId;
}
